import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Grid,
  Divider,
  Stack,
  Badge,
  Fade,
  Zoom,
  alpha,
  Paper,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Swal from "sweetalert2";
import {
  listApprovalRequestsAction,
  approveRequestAction,
  rejectRequestAction,
} from "../../Actions/ApprovalActions";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import CustomSnackbar from "../Common/snackBar";
import { formatDateTime } from "../Common/DateFormat";
import DataTable from "../../components/DataTable/DataTable";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import { decrypt, decryptedData, encrypt } from "../../utils/encryptDecrypt";
import "./ApprovalRequests.css";

const formatApprovalValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return { kind: "empty", label: "— Empty —" };
  }

  if (typeof value === "boolean") {
    return {
      kind: "status",
      label: value ? "Active" : "Inactive",
      active: value,
    };
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return { kind: "status", label: "Active", active: true };
    }
    if (normalized === "false") {
      return { kind: "status", label: "Inactive", active: false };
    }
  }

  if (typeof value === "object") {
    return { kind: "object", value };
  }

  return { kind: "text", label: String(value) };
};

const ApprovalValue = ({ value, emphasize = false }) => {
  const formatted = formatApprovalValue(value);

  if (formatted.kind === "empty") {
    return (
      <Typography
        variant="body2"
        sx={{
          color: "#94a3b8",
          fontStyle: "italic",
          fontSize: "12.5px",
        }}
      >
        {formatted.label}
      </Typography>
    );
  }

  if (formatted.kind === "status") {
    return (
      <Chip
        size="small"
        label={formatted.label}
        sx={{
          height: 24,
          fontWeight: 700,
          fontSize: "11px",
          borderRadius: "999px",
          bgcolor: formatted.active ? "#ecfdf5" : "#f1f5f9",
          color: formatted.active ? "#047857" : "#64748b",
          border: `1px solid ${formatted.active ? "#a7f3d0" : "#e2e8f0"}`,
        }}
      />
    );
  }

  if (formatted.kind === "object") {
    return (
      <Stack spacing={0.75} sx={{ width: "100%" }}>
        {Object.entries(formatted.value).map(([key, nestedValue]) => {
          const nested = formatApprovalValue(nestedValue);
          return (
            <Box
              key={key}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#64748b", fontWeight: 600, fontSize: "11px" }}
              >
                {key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())
                  .trim()}
              </Typography>
              {nested.kind === "status" ? (
                <Chip
                  size="small"
                  label={nested.label}
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: "10.5px",
                    borderRadius: "999px",
                    bgcolor: nested.active ? "#ecfdf5" : "#f1f5f9",
                    color: nested.active ? "#047857" : "#64748b",
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "12px",
                    fontWeight: emphasize ? 600 : 500,
                    color: emphasize ? "#15803d" : "#334155",
                    wordBreak: "break-word",
                  }}
                >
                  {nested.kind === "object"
                    ? JSON.stringify(nested.value)
                    : nested.label}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    );
  }

  return (
    <Typography
      variant="body2"
      sx={{
        fontSize: "12.5px",
        fontWeight: emphasize ? 600 : 500,
        color: emphasize ? "#15803d" : "#334155",
        wordBreak: "break-word",
        width: "100%",
      }}
    >
      {formatted.label}
    </Typography>
  );
};

const CleanStatusChip = ({ status }) => {
  const statusConfig = {
    PENDING: {
      label: "Pending",
      color: "#f59e0b",
      bg: "#fef3c7",
      icon: <PendingActionsIcon sx={{ fontSize: 14 }} />,
    },
    APPROVED: {
      label: "Approved",
      color: "#10b981",
      bg: "#d1fae5",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
    },
    REJECTED: {
      label: "Rejected",
      color: "#ef4444",
      bg: "#fee2e2",
      icon: <CancelOutlinedIcon sx={{ fontSize: 14 }} />,
    },
  };

  const config = statusConfig[status] || statusConfig.PENDING;

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      sx={{
        bgcolor: config.bg,
        color: config.color,
        fontWeight: 700,
        fontSize: "11px",
        height: 26,
        px: 0.5,
        borderRadius: "6px",
        border: `1px solid ${config.color}40`,
        "& .MuiChip-icon": {
          color: config.color,
          ml: 0.5,
        },
      }}
    />
  );
};

const CompactStatsCard = ({ label, value, icon, color, onClick, active }) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: "pointer",
      borderRadius: 2,
      border: "2px solid",
      borderColor: active ? color : "#e5e7eb",
      bgcolor: active ? `${color}08` : "#fff",
      transition: "all 0.2s",
      "&:hover": {
        borderColor: color,
        bgcolor: `${color}08`,
        transform: "translateY(-2px)",
        boxShadow: `0 4px 12px ${color}20`,
      },
    }}
  >
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            bgcolor: `${color}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: color,
              lineHeight: 1,
              mb: 0.5,
            }}
          >
            {value}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              fontSize: "11px",
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const CleanDetailDialog = ({ open, onClose, row }) => {
  if (!row) return null;

  const comparisonData = row.comparison_data || {};
  const hasChanges = Object.keys(comparisonData).length > 0;
  const requesterName =
    `${row?.user?.firstName ? decryptedData(row.user.firstName) : ""} ${
      row?.user?.lastName ? decryptedData(row.user.lastName) : ""
    }`.trim() || "—";

  return (
    <Dialog
      className="approval-dialog"
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 20px 50px -24px rgba(15, 23, 42, 0.35)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 2.5,
          py: 2,
          borderBottom: "1px solid #e5eaf1",
          background:
            "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              bgcolor: "#eef2ff",
              color: "#22419e",
            }}
          >
            <AssignmentIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#0f172a",
                fontSize: "15px",
                lineHeight: 1.25,
              }}
            >
              Request Details
            </Typography>
            <Typography
              sx={{
                color: "#64748b",
                fontSize: "12px",
                mt: 0.25,
              }}
            >
              ID #{row.id} · {row.entity_name}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#64748b",
            bgcolor: "#f1f5f9",
            borderRadius: "10px",
            "&:hover": { bgcolor: "#e2e8f0" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: "100%",
                borderRadius: "14px",
                border: "1px solid #e5eaf1",
                bgcolor: "#fff",
              }}
            >
              <Typography
                sx={{
                  color: "#64748b",
                  fontWeight: 700,
                  fontSize: "11px",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  mb: 1.25,
                }}
              >
                Entity & Action
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={row.entity_name}
                  size="small"
                  sx={{
                    bgcolor: "#eef2ff",
                    color: "#22419e",
                    fontWeight: 700,
                    fontSize: "11px",
                    height: 26,
                    borderRadius: "8px",
                  }}
                />
                <Chip
                  label={String(row.action_type || "")
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                  size="small"
                  sx={{
                    bgcolor: "#f1f5f9",
                    color: "#334155",
                    fontWeight: 700,
                    fontSize: "11px",
                    height: 26,
                    borderRadius: "8px",
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: "100%",
                borderRadius: "14px",
                border: "1px solid #e5eaf1",
                bgcolor: "#fff",
              }}
            >
              <Typography
                sx={{
                  color: "#64748b",
                  fontWeight: 700,
                  fontSize: "11px",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  mb: 1.25,
                }}
              >
                Requested By
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: "#22419e",
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  {requesterName.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#0f172a" }}>
                    {requesterName}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "11.5px" }}>
                    {row.role?.roleName || "—"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 1.5,
            borderRadius: "14px",
            border: "1px solid #e5eaf1",
            bgcolor: "#fff",
          }}
        >
          <Typography
            sx={{
              color: "#64748b",
              fontWeight: 700,
              fontSize: "11px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            Timeline
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 1.5,
                  height: "100%",
                  borderRadius: "12px",
                  bgcolor: "#f8fafc",
                  border: "1px solid #eef2f7",
                }}
              >
                <Typography sx={{ color: "#94a3b8", fontSize: "11px", fontWeight: 600, mb: 1 }}>
                  Status
                </Typography>
                <CleanStatusChip status={row.status} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 1.5,
                  height: "100%",
                  borderRadius: "12px",
                  bgcolor: "#f8fafc",
                  border: "1px solid #eef2f7",
                }}
              >
                <Typography sx={{ color: "#94a3b8", fontSize: "11px", fontWeight: 600, mb: 1 }}>
                  Submitted At
                </Typography>
                <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#0f172a" }}>
                  {row.createdAt ? formatDateTime(row.createdAt) : "—"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 1.5,
                  height: "100%",
                  borderRadius: "12px",
                  bgcolor: "#f8fafc",
                  border: "1px solid #eef2f7",
                }}
              >
                <Typography sx={{ color: "#94a3b8", fontSize: "11px", fontWeight: 600, mb: 1 }}>
                  Reviewed At
                </Typography>
                <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#0f172a" }}>
                  {row.approved_at ? formatDateTime(row.approved_at) : "—"}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {row.status?.toLowerCase() === "rejected" && row.rejection_reason && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: "12px",
                bgcolor: "#fef2f2",
                border: "1px solid #fecaca",
              }}
            >
              <Typography
                sx={{
                  color: "#b91c1c",
                  fontWeight: 700,
                  fontSize: "11px",
                  mb: 0.75,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Rejection Reason
              </Typography>
              <Typography sx={{ color: "#7f1d1d", fontSize: "13px" }}>
                {row.rejection_reason}
              </Typography>
            </Box>
          )}
        </Paper>

        {hasChanges && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "14px",
              border: "1px solid #e5eaf1",
              bgcolor: "#fff",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.75 }}>
              <CompareArrowsIcon sx={{ color: "#22419e", fontSize: 18 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "13.5px", color: "#0f172a" }}>
                Requested Changes
              </Typography>
            </Box>

            <Stack spacing={1.5}>
              {Object.entries(comparisonData).flatMap(
                ([sectionName, sectionFields]) => {
                  const isNormalField =
                    sectionFields &&
                    typeof sectionFields === "object" &&
                    ("old" in sectionFields || "new" in sectionFields);

                  const fieldsToRender = isNormalField
                    ? [[sectionName, sectionFields]]
                    : Object.entries(sectionFields || {});

                  return fieldsToRender.map(([fieldName, fieldData]) => (
                    <Box
                      key={`${sectionName}-${fieldName}`}
                      sx={{
                        p: 1.75,
                        borderRadius: "12px",
                        border: "1px solid #e8eef8",
                        bgcolor: "#f8faff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#22419e",
                            fontSize: "12.5px",
                          }}
                        >
                          {fieldName
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())
                            .trim()}
                        </Typography>
                        <Chip
                          label="Changed"
                          size="small"
                          sx={{
                            height: 22,
                            bgcolor: "#eef2ff",
                            color: "#22419e",
                            fontWeight: 700,
                            fontSize: "10px",
                            borderRadius: "999px",
                          }}
                        />
                      </Box>

                      <Grid container spacing={1.25}>
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              p: 1.5,
                              minHeight: 72,
                              borderRadius: "10px",
                              bgcolor: "#fff",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#64748b",
                                fontWeight: 700,
                                display: "block",
                                mb: 1,
                                fontSize: "10px",
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                              }}
                            >
                              Current Value
                            </Typography>
                            <ApprovalValue value={fieldData?.old} />
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              p: 1.5,
                              minHeight: 72,
                              borderRadius: "10px",
                              bgcolor: "#f0fdf4",
                              border: "1px solid #bbf7d0",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#15803d",
                                fontWeight: 700,
                                display: "block",
                                mb: 1,
                                fontSize: "10px",
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                              }}
                            >
                              Requested Value
                            </Typography>
                            <ApprovalValue value={fieldData?.new} emphasize />
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  ));
                }
              )}
            </Stack>
          </Paper>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          bgcolor: "#fff",
          borderTop: "1px solid #e5eaf1",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            borderColor: "#dbe3ef",
            color: "#22419e",
            "&:hover": {
              borderColor: "#22419e",
              bgcolor: "#eef2ff",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CleanRejectDialog = ({ open, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState("");
  const handleConfirm = () => {
    onConfirm(reason);
    setReason("");
  };
  const handleClose = () => {
    setReason("");
    onClose();
  };

  return (
    <Dialog
      className="approval-dialog"
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <Box sx={{ bgcolor: "#ef4444", p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 36, height: 36 }}>
            <ThumbDownAltIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#fff" }}>
              Reject Request
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)" }}>
              Provide a reason for rejection
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Rejection Reason"
          placeholder="Explain why this request is being rejected..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: "#f8fafc" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          size="small"
          disabled={loading || !reason.trim()}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
        >
          Confirm Reject
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ApprovalRequests = () => {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listApprovalRequestsAction(
        page,
        pageSize,
        statusFilter,
        searchValue
      );
      const payload = res?.data;
      const rowsData = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      setRows(rowsData);
      setTotalRecords(res?.totalRecords || payload?.totalRecords || 0);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.message || "Failed to fetch requests",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, statusFilter, searchValue]);

  const handleApprove = async (row) => {
    const result = await Swal.fire({
      title: "Approve Request?",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Entity:</strong> ${row.entity_name}</p>
          <p><strong>Action:</strong> ${row.action_type}</p>
          <p><strong>Requested By:</strong> ${row?.user?.firstName ? decryptedData(row.user.firstName) : ""
        } ${row?.user?.lastName ? decryptedData(row.user.lastName) : ""}</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#94a3b8",
      confirmButtonText: "Yes, Approve",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setActionLoading(true);
    try {
      await approveRequestAction(row.id);
      setSnackbar({
        open: true,
        message: "Request approved successfully!",
        severity: "success",
      });
      fetchData();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.message || "Failed to approve request",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async (reason) => {
    setActionLoading(true);
    try {
      await rejectRequestAction(rejectRow.id, reason);
      setRejectRow(null);
      setSnackbar({
        open: true,
        message: "Request rejected successfully",
        severity: "success",
      });
      fetchData();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.message || "Failed to reject request",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleTableAction = (action, row) => {
    switch (action) {
      case "view":
        setDetailRow(row);
        break;
      case "approve":
        if (row.status === "PENDING") handleApprove(row);
        break;
      case "reject":
        if (row.status === "PENDING") setRejectRow(row);
        break;
      default:
        break;
    }
  };

  const handleFilterApply = (values) => {
    setSearchValue(values.search || "");
    setStatusFilter(values.status || "");
    setPage(1);
  };

  const handleFilterReset = () => {
    setSearchValue("");
    setStatusFilter("");
    setPage(1);
  };

  const pendingCount = rows.filter((r) => r.status === "PENDING").length;
  const approvedCount = rows.filter((r) => r.status === "APPROVED").length;
  const rejectedCount = rows.filter((r) => r.status === "REJECTED").length;

  const columns = [
    {
      field: "entity_name",
      headerName: "Entity",
      minWidth: 130,
      flex: 1,
      renderCell: ({ row }) => (
        <Chip
          label={row.entity_name}
          size="small"
          sx={{
            bgcolor: "#eef2ff",
            color: "#22419e",
            fontWeight: 700,
            fontSize: "11.5px",
            height: 26,
            borderRadius: "8px",
          }}
        />
      ),
    },
    {
      field: "action_type",
      headerName: "Action",
      minWidth: 140,
      flex: 0.9,
      renderCell: ({ row }) => {
        const actionColors = {
          CREATE: "#047857",
          UPDATE: "#b45309",
          DELETE: "#b91c1c",
          WORKFLOW: "#6d28d9",
          STATUS_UPDATE: "#22419e",
        };
        const color = actionColors[row.action_type] || "#475569";
        const label = String(row.action_type || "")
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase());

        return (
          <Chip
            label={label}
            size="small"
            sx={{
              bgcolor: `${color}14`,
              color,
              fontWeight: 700,
              fontSize: "11px",
              height: 26,
              borderRadius: "8px",
              border: `1px solid ${color}28`,
            }}
          />
        );
      },
    },
    {
      field: "user",
      headerName: "Requested By",
      minWidth: 180,
      flex: 1.2,
      renderCell: ({ row }) => {
        const fullName = `${row?.user?.firstName ? decryptedData(row.user.firstName) : ""
          } ${row?.user?.lastName ? decryptedData(row.user.lastName) : ""}`.trim();

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Avatar
              sx={{
                width: 30,
                height: 30,
                bgcolor: "#22419e",
                fontSize: "11px",
                fontWeight: 700,
                borderRadius: "9px",
              }}
            >
              {fullName?.charAt(0)?.toUpperCase() || "?"}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#0f172a", lineHeight: 1.25 }}>
                {fullName || "—"}
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: "11px", lineHeight: 1.3 }}>
                {row.role?.roleName || "—"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "created_at",
      headerName: "Submitted",
      minWidth: 160,
      flex: 1,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <AccessTimeIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
          <Typography sx={{ color: "#64748b", fontSize: "12.5px", fontWeight: 500 }}>
            {row.createdAt ? formatDateTime(row.createdAt, true) : "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.8,
      renderCell: ({ row }) => <CleanStatusChip status={row.status} />,
    },
  ];

  const filters = [
    { type: "search", name: "search", placeholder: "Search requests..." },
    {
      type: "select",
      name: "status",
      label: "Status",
      defaultValue: "",
      options: [
        { label: "All", value: "" },
        { label: "Pending", value: "PENDING" },
        { label: "Approved", value: "APPROVED" },
        { label: "Rejected", value: "REJECTED" },
      ],
    },
  ];

  return (
    <Box className="approval-page" sx={{ minHeight: "100vh", pb: 4 }}>
      <div id="kt_content_container">
        <BreadcrumbsComponent />
        <Box>
          {actionLoading && <LinearProgress />}
          <DataTable
            columns={columns}
            rows={rows}
            loading={loading}
            totalCount={totalRecords}
            page={page}
            pageSize={pageSize}
            filters={filters}
            filterValues={{ search: searchValue, status: statusFilter }}
            onFilterApply={handleFilterApply}
            onFilterReset={handleFilterReset}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
            onAction={handleTableAction}
            actions={{
              view: true,
              approve: true,
              reject: true,
            }}
          />
        </Box>

        <CleanDetailDialog
          open={Boolean(detailRow)}
          onClose={() => setDetailRow(null)}
          row={detailRow}
        />

        <CleanRejectDialog
          open={Boolean(rejectRow)}
          onClose={() => setRejectRow(null)}
          onConfirm={handleRejectConfirm}
          loading={actionLoading}
        />

        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        />
      </div>
    </Box>
  );
};

export default ApprovalRequests;